import styled from '@emotion/styled'
import {
  Divider,
  MuiMenu as Menu,
  MenuItem,
  Icon,
  Avatar,
  Theme,
  Typography,
  Grid,
  Radio,
} from '@yuno/design-system-web'
import { Organization, User } from '@yuno/types'

type ThemeProp = {
  theme?: Theme
}

export type AccountProp = {
  name: string
  principal: boolean
  code: string
}

export type AccountMenuProps = {
  accounts: Organization.Account[]
  handleSection: (breadcrumb: any) => void
  profile?: User.UserResponsePayload
  hidden?: boolean
  handleSwitch?: () => void
  checkedSwitch?: boolean
}

export type AccountMenuItemNameProps = {
  name: string
  profile?: User.UserResponsePayload
  selectedAccount: AccountProp
}

export const FIRST_TAB = 0
export const SECOND_TAB = 1

export const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const AvatarStyled = styled(Avatar)(
  ({ theme, noHover }: { theme?: Theme; noHover?: boolean }) => ({
    cursor: 'pointer',
    color: `${theme.palette.neutral.white[0]}`,
    background: `${theme.palette.vivid.coral}`,
    marginRight: '12px',
    '&:hover': {
      boxShadow: noHover ? 'none' : `0 0 0px 3px ${theme.palette.grey[2]}`,
    },
  }),
)

export const StyledName = styled(Typography)(({ theme }: ThemeProp) => ({
  ...theme.typography.small.regular,
  color: theme.palette.neutral.black,
  marginLeft: 10,
  marginRight: 10,
  cursor: 'pointer',
  display: 'block',
}))

export const StyledRole = styled(Typography)(({ theme }: ThemeProp) => ({
  ...theme.typography.tiny.regular,
  color: theme.palette.grey[5],
  marginLeft: 10,
  marginRight: 10,
  cursor: 'pointer',
}))

export const SeeAllAccounts = styled('span')(({ theme }: ThemeProp) => ({
  ...theme.typography.tiny.regular,
  color: theme.palette.neutral.black,
  cursor: 'pointer',
}))

export const AccountsCounter = styled('span')(({ theme }: ThemeProp) => ({
  ...theme.typography.tiny.regular,
  color: theme.palette.neutral.white[0],
  backgroundColor: theme.palette.primary.main,
  cursor: 'pointer',
  padding: '1px 6.5px',
  borderRadius: '4px',
  height: '17px',
  minWidth: '19px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
}))

export const ContentContainer = styled.div`
  padding: 20px 0 10px;
`

export const AccountsContainer = styled(Grid)`
  padding: 10px 0;
  overflow-x: hidden;
`

export const MenuHeaderContainerStyled = styled.div<ThemeProp>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[2]};
`

export const StyledMenuHeaderLabel = styled('span')(({ theme }: ThemeProp) => ({
  ...theme.typography.small.regular,
  color: theme.palette.neutral.black,
  flexGrow: 1,
  padding: '0 18px',
}))

export const StyledMenuItemLabel = styled('span')(({ theme }: ThemeProp) => ({
  ...theme.typography.tiny.regular,
  color: theme.palette.neutral.black,
}))

export const StyleAccountContainer = styled.div<ThemeProp>`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.palette.grey[2]};
  background: ${({ theme }) => theme.palette.grey[0]};
  margin: 0 20px 10px 20px;
`

export const CustomLabelStyled = styled.div<ThemeProp>`
  width: 163px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const CustomLabelContainerStyled = styled.div<ThemeProp>`
  border-top: 1px solid ${({ theme }) => theme.palette.grey[2]};
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[2]};
  padding-top: 14px;
  padding-bottom: 14px;
`

export const StyledAccount = styled.div<ThemeProp>`
  padding: 10px 16px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[2]};
`

export const AccountListItem = styled(Grid)<ThemeProp>`
  display: flex;
  align-items: center;
  padding: 10px 20px;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grey[1]};
  }

  > span {
    flex-grow: 1;
  }

  > label {
    margin: 0;

    > span:first-child {
      margin-right: -8px;
    }
  }
`

export const SeeAllAccountsContainer = styled(StyledAccount)`
  justify-content: space-between;
  border-bottom: none;
`

export const AccountMenuItemStyled = styled(MenuItem)(() => ({
  '&.MuiButtonBase-root.MuiMenuItem-root': {
    padding: '10px 20px',
    height: '35px',
  },
}))

export const MenuStyled = styled(Menu)(() => ({
  zIndex: 9999,
  marginTop: '13px',
  '&.menu-account .MuiList-root': {
    border: 0,
    padding: 0,
  },
}))

export const IconStyled = styled(Icon)(({ theme }: { theme?: Theme }) => ({
  color: theme.palette.grey[5],
  cursor: 'pointer',
}))

export const DividerStyled = styled(Divider)<ThemeProp>`
  border-color: ${({ theme }) => `1px solid ${theme.palette.grey[2]}`};
  margin: 10px 20px 0 20px;
`

export const ManageYourAccountsLink = styled.div<ThemeProp>(({ theme }) => ({
  ...theme.typography.tiny.regular,
  flexGrow: 1,
}))

export const ManageYourAccountsLinkContainer = styled.div`
  display: flex;
  padding: 20px 20px 10px 20px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`
export const RadioStyled = styled(Radio)`
  margin-left: -5px;
`
